import { Directive, ElementRef, Input, OnInit, OnDestroy, Renderer2, inject } from '@angular/core';

/**
 * Directive that synchronizes the position of an overlay element with a source HTML element.
 * Uses continuous requestAnimationFrame monitoring to track all bounding box changes including:
 * - Position changes (scroll, DOM reordering, transforms, animations)
 * - Size changes (resize, content changes, layout shifts)
 */
@Directive({
  selector: '[nrcTrackHtmlElement]',
  standalone: true
})
export class TrackHtmlElementDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  @Input({ required: true }) public nrcTrackHtmlElement!: HTMLElement;

  private requestAnimationFrameId?: number | null;
  private previousBoundingRect?: DOMRect | null;

  /**
   * Initialize continuous position tracking on directive initialization.
   */
  public ngOnInit(): void {
    this.startTracking();
  }

  /**
   * Clean up and cancel any pending animation frames.
   */
  public ngOnDestroy(): void {
    this.stopTracking();
  }

  /**
   * Starts continuous monitoring of the source element's bounding box.
   * Uses requestAnimationFrame to check for changes on every frame.
   */
  private startTracking(): void {
    const sourceElement = this.nrcTrackHtmlElement;

    if (!sourceElement) {
      return;
    }

    // Initial position update
    this.updatePosition();

    // Start continuous monitoring loop
    this.monitorBoundingBox();
  }

  /**
   * Continuous monitoring loop that checks for bounding box changes every frame.
   * Only updates the DOM when actual changes are detected.
   */
  private monitorBoundingBox(): void {
    const sourceElement = this.nrcTrackHtmlElement;

    if (!sourceElement) {
      return;
    }

    const currentRect = sourceElement.getBoundingClientRect();

    // Check if bounding box has changed
    if (this.hasBoundingBoxChanged(currentRect)) {
      this.updatePosition(currentRect);
      this.previousBoundingRect = currentRect;
    }

    // Schedule next check
    this.requestAnimationFrameId = requestAnimationFrame(() => this.monitorBoundingBox());
  }

  /**
   * Checks if the bounding box has changed compared to the last known state.
   * Compares position (x, y) and size (width, height).
   * @param currentRect - The current bounding rectangle to compare.
   * @returns True if the bounding box has changed, false otherwise.
   */
  private hasBoundingBoxChanged(currentRect: DOMRect): boolean {
    if (!this.previousBoundingRect) {
      return true;
    }

    return (
      this.previousBoundingRect.x !== currentRect.x ||
      this.previousBoundingRect.y !== currentRect.y ||
      this.previousBoundingRect.width !== currentRect.width ||
      this.previousBoundingRect.height !== currentRect.height
    );
  }

  /**
   * Updates the overlay position and size to match the source element.
   * Uses Renderer2 for SSR compatibility.
   * @param boundingRect - Optional pre-computed bounding rectangle to avoid redundant calculations.
   */
  private updatePosition(boundingRect?: DOMRect): void {
    const sourceElement = this.nrcTrackHtmlElement;

    if (!sourceElement) {
      return;
    }

    const _boundingRect = boundingRect || sourceElement.getBoundingClientRect();
    const overlayElement = this.elementRef.nativeElement;

    // Update position and size using Renderer2 for SSR compatibility
    this.renderer.setStyle(overlayElement, 'left', `${_boundingRect.x}px`);
    this.renderer.setStyle(overlayElement, 'top', `${_boundingRect.y}px`);
    this.renderer.setStyle(overlayElement, 'width', `${_boundingRect.width}px`);
    this.renderer.setStyle(overlayElement, 'height', `${_boundingRect.height}px`);
  }

  /**
   * Stops continuous monitoring and cancels pending animation frames.
   */
  private stopTracking(): void {
    if (this.requestAnimationFrameId) {
      cancelAnimationFrame(this.requestAnimationFrameId);

      this.requestAnimationFrameId = null;
    }

    this.previousBoundingRect = null;
  }
}
